import type { QueryStatus } from "@tanstack/react-query";
import { useCallback, useSyncExternalStore } from "react";

import { auth } from "@/services/firebase.auth";
import {
  subscribeToDocument,
  subscribeToQuery,
  type QuerySource,
} from "@/services/firebase-documents.runtime";

import {
  pending,
  subscriptions,
  type Snapshot,
} from "./document-subscriptions";
export { clearDocumentSubscriptions } from "./document-subscriptions";

/** Share one listener per account/document; retained messages live until account cleanup. */
export function useDocSnap<T>(
  path: string,
  {
    enabled = true,
    keepAlive = false,
    initialValue,
  }: { enabled?: boolean; keepAlive?: boolean; initialValue?: T } = {},
): [T | undefined, QueryStatus, unknown] {
  const uid = auth.currentUser?.uid;
  const key = enabled && uid && path ? JSON.stringify([uid, "doc", path]) : "";

  const start = useCallback(
    (publish: (value: Snapshot) => void) =>
      subscribeToDocument(path, publish, uid, initialValue),
    [path, uid, initialValue],
  );

  return useSharedSnapshot<T>(key, start, keepAlive);
}

/** Queries share listeners and are cleared by the same account-boundary cleanup. */
export function useQuerySnap<T>(
  queryKey: string,
  source: QuerySource | undefined,
): [Array<T & { id: string }> | undefined, QueryStatus, unknown] {
  const uid = auth.currentUser?.uid;
  const key = uid && source ? JSON.stringify([uid, "query", queryKey]) : "";
  const start = useCallback(
    (publish: (value: Snapshot) => void) => {
      if (!source) {
        return () => {};
      }
      return subscribeToQuery(source, publish);
    },
    [source],
  );
  return useSharedSnapshot<Array<T & { id: string }>>(key, start);
}

function useSharedSnapshot<T>(
  key: string,
  start: (publish: (value: Snapshot) => void) => () => void,
  keepAlive = false,
): [T | undefined, QueryStatus, unknown] {
  const subscribe = useCallback(
    (notify: () => void) => {
      if (!key) {
        return () => {};
      }
      let subscription = subscriptions.get(key);
      if (!subscription) {
        subscription = { snapshot: pending, listeners: new Set() };
        subscriptions.set(key, subscription);
      }
      const entry = subscription;
      entry.keepAlive ||= keepAlive;
      entry.listeners.add(notify);
      if (!entry.stop) {
        const publish = (snapshot: Snapshot) => {
          // Ignore events from an old account or a listener already removed.
          if (subscriptions.get(key) !== entry) {
            return;
          }
          entry.snapshot = snapshot;
          entry.listeners.forEach((listener) => listener());
        };
        entry.stop = start(publish);
      }
      return () => {
        entry.listeners.delete(notify);
        if (!entry.listeners.size && !entry.keepAlive) {
          entry.stop?.();
          if (subscriptions.get(key) === entry) {
            subscriptions.delete(key);
          }
        }
      };
    },
    [key, start, keepAlive],
  );
  const getSnapshot = useCallback(
    () => subscriptions.get(key)?.snapshot ?? pending,
    [key],
  );
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, () => pending);
  return [snapshot.value as T | undefined, snapshot.status, snapshot.error];
}
